import { Ionicons } from '@expo/vector-icons';
import { router, type Href } from 'expo-router';
import React, { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { Modal } from '@/components/ui/modal';
import {
	clearNotificationHistory,
	markNotificationHistoryRead,
	useNotificationHistory,
	type NotificationHistoryEntry,
} from '@/services/notification-history';

const VARIANT_COLOR: Record<NotificationHistoryEntry['variant'], string> = {
	success: '#2e7d32',
	error: '#c62828',
	warning: '#ef6c00',
	info: '#1976d2',
};

/**
 * The bell that reopens past toasts (#633): an unread count, and a panel listing the per-user
 * history newest first. Opening the panel marks everything read; an entry with a link opens it.
 */
export const NotificationHistoryBell: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
	const history = useNotificationHistory();
	const [open, setOpen] = useState(false);
	const unread = history.filter((e) => !e.read).length;

	const show = () => {
		setOpen(true);
		markNotificationHistoryRead();
	};

	const follow = (entry: NotificationHistoryEntry) => {
		if (!entry.href) return;
		setOpen(false);
		router.push(entry.href as Href);
	};

	return (
		<>
			<Pressable
				onPress={show}
				accessibilityLabel={unread ? `Notifications, ${unread} unread` : 'Notifications'}
				style={[styles.bell, compact && styles.bellCompact]}
				testID="notification-history-bell"
			>
				<Ionicons name={unread ? 'notifications' : 'notifications-outline'} size={22} color="#37474f" />
				{unread > 0 && (
					<View style={styles.badge} testID="notification-history-unread">
						<Text style={styles.badgeLabel}>{unread > 99 ? '99+' : unread}</Text>
					</View>
				)}
			</Pressable>
			<Modal
				visible={open}
				onDismiss={() => setOpen(false)}
				dismissOnBackdrop
				contentContainerStyle={styles.panel}
			>
				<View style={styles.header}>
					<Text style={styles.title}>Notifications</Text>
					{history.length > 0 && (
						<Pressable onPress={clearNotificationHistory} testID="notification-history-clear">
							<Text style={styles.link}>Clear all</Text>
						</Pressable>
					)}
					<Pressable onPress={() => setOpen(false)} hitSlop={10} testID="notification-history-close">
						<Text style={styles.close}>✕</Text>
					</Pressable>
				</View>
				<ScrollView contentContainerStyle={styles.list} testID="notification-history-list">
					{history.length === 0 ? (
						<Text style={styles.empty}>Nothing yet. Confirmations you have seen will collect here.</Text>
					) : history.map((entry) => (
						<Pressable
							key={entry.id}
							onPress={() => follow(entry)}
							disabled={!entry.href}
							style={styles.entry}
							testID={`notification-history-entry-${entry.id}`}
						>
							<View style={[styles.dot, { backgroundColor: VARIANT_COLOR[entry.variant] ?? VARIANT_COLOR.info }]} />
							<View style={styles.flex1}>
								<Text style={styles.message}>{entry.message}</Text>
								<Text style={styles.meta}>
									{[entry.source, new Date(entry.createdAt).toLocaleString()].filter(Boolean).join(' · ')}
								</Text>
							</View>
							{!!entry.href && <Ionicons name="chevron-forward" size={16} color="#90a4ae" />}
						</Pressable>
					))}
				</ScrollView>
			</Modal>
		</>
	);
};

const styles = StyleSheet.create({
	bell: { padding: 6, borderRadius: 8 },
	bellCompact: { alignSelf: 'center' },
	badge: {
		position: 'absolute', top: 0, right: 0, minWidth: 16, height: 16, borderRadius: 8,
		backgroundColor: '#c62828', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 3,
	},
	badgeLabel: { color: '#fff', fontSize: 10, fontWeight: '700' },
	panel: { width: '94%', maxWidth: 480, maxHeight: '80%', borderRadius: 12, padding: 0 },
	header: {
		flexDirection: 'row', alignItems: 'center', gap: 12, padding: 16,
		borderBottomWidth: 1, borderBottomColor: '#eee',
	},
	title: { flex: 1, fontSize: 17, fontWeight: '700', color: '#212121' },
	link: { fontSize: 12, color: '#1976d2', fontWeight: '600' },
	close: { fontSize: 18, color: '#888', paddingHorizontal: 4 },
	list: { padding: 8 },
	empty: { fontSize: 13, color: '#90a4ae', padding: 16, textAlign: 'center' },
	entry: {
		flexDirection: 'row', alignItems: 'center', gap: 10, padding: 10,
		borderBottomWidth: 1, borderBottomColor: '#f3f3f3',
	},
	dot: { width: 8, height: 8, borderRadius: 4 },
	flex1: { flex: 1, minWidth: 0 },
	message: { fontSize: 14, color: '#263238' },
	meta: { fontSize: 11, color: '#90a4ae', marginTop: 2 },
});
